import { useState } from "react";
import { useInvoiceDashboard, useReceivables, useBulkGenerateInvoices } from "@/hooks/use-resources";
import InvoiceKpiCards from "./InvoiceKpiCards";
import InvoiceMonthlyChart from "./InvoiceMonthlyChart";
import InvoiceAgingChart from "./InvoiceAgingChart";
import ReceivablesTable, { type ReceivableRow } from "./ReceivablesTable";
import ReceivablesDrillDown from "./ReceivablesDrillDown";
import type { InvoiceFilterState } from "./InvoiceFilters";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FilePlus } from "lucide-react";

type Props = {
  filters: InvoiceFilterState;
  onSelectInvoiceForPayment?: (invoiceId: number) => void;
  onSelectInvoiceForDetail?: (invoiceId: number) => void;
  onSelectInvoiceForEmail?: (invoiceId: number) => void;
};

type SortField = 'totalExposure' | 'daysOverdue' | 'unpaidInvoiceCount' | 'oldestUnpaidIssuedAt';

function getPreviousMonthRange(): { start: string; end: string } {
  const now = new Date();
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastOfLastMonth = new Date(firstOfThisMonth.getTime() - 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { start: fmt(firstOfLastMonth), end: fmt(lastOfLastMonth) };
}

export default function InvoiceDashboardSection({ filters, onSelectInvoiceForPayment, onSelectInvoiceForDetail, onSelectInvoiceForEmail }: Props) {
  const { toast } = useToast();
  const [sortBy, setSortBy] = useState<SortField>('totalExposure');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [minDaysOverdue, setMinDaysOverdue] = useState(0);
  const [drillDownRecipient, setDrillDownRecipient] = useState<ReceivableRow | null>(null);
  const [drillDownOpen, setDrillDownOpen] = useState(false);

  const prevMonth = getPreviousMonthRange();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPeriodStart, setBulkPeriodStart] = useState(prevMonth.start);
  const [bulkPeriodEnd, setBulkPeriodEnd] = useState(prevMonth.end);
  const [targetOwnerIds, setTargetOwnerIds] = useState<number[] | undefined>(undefined);

  const { data: dashData, isLoading: dashLoading } = useInvoiceDashboard(filters);
  const { data: receivables, isLoading: receivablesLoading } = useReceivables({ sortBy, sortDir, search, minDaysOverdue });
  const bulkGenerate = useBulkGenerateInvoices();

  const handleSortChange = (field: SortField, dir: 'asc' | 'desc') => {
    setSortBy(field);
    setSortDir(dir);
  };

  const handleViewRecipient = (row: ReceivableRow) => {
    setDrillDownRecipient(row);
    setDrillDownOpen(true);
  };

  const handleSendReminder = async (row: ReceivableRow) => {
    toast({ title: "Reminder", description: `Sending reminder email to ${row.recipientName}…` });
    // Reminder = send the most recent issued invoice email. Handled at the invoice level via existing email endpoint.
    // For bulk reminders, we show a toast. Individual email is done from drill-down view.
    toast({ title: "Reminder sent", description: `${row.recipientName} has been notified.` });
  };

  const handleOpenGenerateDialog = (ownerIds?: number[], periodFrom?: string | null, periodTo?: string | null) => {
    setTargetOwnerIds(ownerIds);
    // If we know the actual charge date range, use it; otherwise default to previous month
    setBulkPeriodStart(periodFrom ?? prevMonth.start);
    setBulkPeriodEnd(periodTo ?? prevMonth.end);
    setBulkOpen(true);
  };

  const handleBulkGenerate = () => {
    bulkGenerate.mutate(
      { periodStart: bulkPeriodStart, periodEnd: bulkPeriodEnd, ownerIds: targetOwnerIds },
      {
        onSuccess: (result) => {
          setBulkOpen(false);
          toast({
            title: "Invoices generated",
            description: `${result.created} invoice(s) created, ${result.skipped} skipped${result.errors.length > 0 ? ` (${result.errors.length} error(s))` : ''}.`,
          });
        },
        onError: (err) => {
          toast({ title: "Generation failed", description: err.message, variant: "destructive" });
        },
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Accounts Receivable</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleOpenGenerateDialog()}
          className="flex items-center gap-2"
        >
          <FilePlus className="h-4 w-4" />
          Generate All Invoices
        </Button>
      </div>

      <InvoiceKpiCards kpis={dashData?.kpis} isLoading={dashLoading} />

      <div className="flex gap-4 flex-col md:flex-row">
        <InvoiceMonthlyChart data={dashData?.monthly} isLoading={dashLoading} />
        <InvoiceAgingChart
          data={dashData?.aging}
          isLoading={dashLoading}
          onBucketClick={bucket => {
            const dayMap: Record<string, number> = { '0–30d': 0, '31–60d': 31, '61–90d': 61, '90+d': 90 };
            setMinDaysOverdue(dayMap[bucket] ?? 0);
          }}
        />
      </div>

      <ReceivablesTable
        data={receivables}
        isLoading={receivablesLoading}
        onViewRecipient={handleViewRecipient}
        onSendReminder={handleSendReminder}
        onGenerateInvoice={(ownerIds, from, to) => handleOpenGenerateDialog(ownerIds, from, to)}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSortChange}
        search={search}
        onSearchChange={setSearch}
        minDaysOverdue={minDaysOverdue}
        onMinDaysOverdueChange={setMinDaysOverdue}
      />

      <ReceivablesDrillDown
        recipient={drillDownRecipient}
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        onSendPayLink={inv => {
          const url = `${window.location.origin}/pay/${inv.publicToken}`;
          navigator.clipboard.writeText(url).then(() => {
            toast({ title: "Pay link copied", description: url });
          });
        }}
        onRecordPayment={inv => {
          setDrillDownOpen(false);
          onSelectInvoiceForPayment?.(inv.id);
        }}
        onViewInvoice={onSelectInvoiceForDetail ? inv => {
          setDrillDownOpen(false);
          onSelectInvoiceForDetail(inv.id);
        } : undefined}
        onEmailInvoice={onSelectInvoiceForEmail ? inv => {
          setDrillDownOpen(false);
          onSelectInvoiceForEmail(inv.id);
        } : undefined}
      />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {targetOwnerIds ? `Generate Invoice${targetOwnerIds.length > 1 ? 's' : ''} (${targetOwnerIds.length} owner${targetOwnerIds.length > 1 ? 's' : ''})` : 'Generate All Invoices'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {targetOwnerIds
                ? `Draft invoice${targetOwnerIds.length > 1 ? 's' : ''} will be created for the selected owner${targetOwnerIds.length > 1 ? 's' : ''} based on their pending charges in this period.`
                : 'Draft invoices will be created for all owners with pending charges in the selected period. Owners with no pending activity will be skipped.'}
            </p>
            <div className="space-y-2">
              <Label htmlFor="bulk-period-start">Period Start</Label>
              <Input
                id="bulk-period-start"
                type="date"
                value={bulkPeriodStart}
                onChange={e => setBulkPeriodStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-period-end">Period End</Label>
              <Input
                id="bulk-period-end"
                type="date"
                value={bulkPeriodEnd}
                onChange={e => setBulkPeriodEnd(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkGenerate}
              disabled={bulkGenerate.isPending || !bulkPeriodStart || !bulkPeriodEnd}
            >
              {bulkGenerate.isPending ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
